import React, { useCallback } from 'react';
import { Modal, ModalProps } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ControlledModal } from '../../common/ControlledModal';
import { useFileExplorerContext } from '../../../hooks/useFileExplorerContext';
import { folderSchema, FolderSchema } from '../Forms/schemas';
import { FolderForm } from '../Forms/FolderForm';
import { createFolder, TreeFolder } from '../services';
import { useFileExplorerMutation } from '../../../hooks/useFileExplorerMutation';

const GENERIC_ERROR_MESSAGE = 'Ocurrió un error al crear la carpeta, por favor intenta nuevamente.';

export function UploadFolderModal({ show, onHide }: ModalProps) {
  const { activeFolder, addNewlyAddedItems } = useFileExplorerContext();
  const contextDescription = `Estás creando una nueva carpeta en ${activeFolder?.name || 'la raíz'}`;
  const form = useForm<FolderSchema>({
    values: {
      name: '',
      description: '',
      parent: activeFolder?.id ?? null,
      remove_file_prefix: false,
    },
    resolver: zodResolver(folderSchema),
  });

  const handleSuccess = useCallback(() => {
    onHide?.();
    form.reset();
  }, [onHide, form]);

  const mutationFn = (data: FolderSchema) => createFolder(data);
  const {
    mutateAsync, isLoading, reset,
  } = useFileExplorerMutation({
    mutationFn,
    onSuccess: handleSuccess,
  });

  const handleCreate = (data: FolderSchema) => {
    const createPromise = mutateAsync(data, {
      onSuccess(response) {
        const folder = response as TreeFolder;
        addNewlyAddedItems([folder.id]);
      },
    });
    toast.promise(createPromise, {
      loading: 'Creando carpeta...',
      success: 'Carpeta creada exitosamente',
      error: {
        message: 'Error al crear la carpeta',
        description: GENERIC_ERROR_MESSAGE,
      },
    });
  };
  const handleModalClose = () => {
    reset();
    onHide?.();
    form.reset();
  };
  return (
    <ControlledModal
      centered
      show={show}
      onHide={handleModalClose}
      title="Crear carpeta"
      description={contextDescription}
      closeButton
    >
      <Modal.Body>
        <FolderForm form={form} onSubmit={handleCreate}>
          <ControlledModal.Actions
            handleClose={handleModalClose}
            isPending={isLoading}
            cancelButton
          />
        </FolderForm>
      </Modal.Body>
    </ControlledModal>
  );
}
